import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  FaArrowLeft, FaSpinner, FaTruck, FaMapMarkerAlt, FaBox, FaWeightHanging,
  FaCalendarAlt, FaEye, FaFileAlt, FaCheckCircle, FaClipboardList
} from 'react-icons/fa';
import ClientLayout from '../layouts/ClientLayout';
import { jobUnits } from '../utils/jobUnits';

// ============================================
// MY JOB — the client's single-job page.
// Opens from the "View" button on My Jobs. Shows what is happening on the job and every
// LOAD it is made up of. Deliberately NO prices beyond the client's own rate, and NEVER
// the subcontractor or what we pay them (the API strips those server-side).
// ============================================

const STATUS_META = {
  pending: { label: 'Pending', color: '#b45309', bg: '#fffbeb' },
  new: { label: 'New', color: '#0A1929', bg: '#f1f5f9' },
  scheduled: { label: 'Scheduled', color: '#7c3aed', bg: '#f5f3ff' },
  active: { label: 'In Progress', color: '#2563eb', bg: '#eff6ff' },
  in_progress: { label: 'In Progress', color: '#2563eb', bg: '#eff6ff' },
  delivered: { label: 'Delivered', color: '#16a34a', bg: '#f0fdf4' },
  completed: { label: 'Completed', color: '#16a34a', bg: '#f0fdf4' },
  paused: { label: 'Paused', color: '#b45309', bg: '#fffbeb' },
  cancelled: { label: 'Cancelled', color: '#6b7280', bg: '#f3f4f6' }
};

// Same order the admin Loads page uses, so the two can never disagree.
const LOAD_STAGES = ['pending', 'on_route_to_loading', 'onsite', 'loading', 'en_route', 'at_destination', 'delivered', 'completed'];
const LOAD_LABELS = {
  pending: 'Pending', on_route_to_loading: 'To Loading', onsite: 'On Site', loading: 'Loading',
  en_route: 'En Route', at_destination: 'At Destination', delivered: 'Delivered', completed: 'Completed'
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }) : 'TBC');
const stageIndex = (s) => Math.max(LOAD_STAGES.indexOf(String(s || '').toLowerCase()), 0);

// A slim pickup -> delivery track with a truck sitting at the current stage.
function LoadProgress({ status }) {
  const idx = stageIndex(status);
  const pct = Math.round((idx / (LOAD_STAGES.length - 1)) * 100);
  return (
    <div className="mt-2">
      <div className="relative h-1.5 rounded-full bg-gray-100">
        <div className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${pct}%`, backgroundColor: '#00A86B' }} />
        <span className="absolute -top-2.5 text-xs" style={{ left: `calc(${pct}% - 8px)` }}>🚛</span>
      </div>
      <div className="flex justify-between text-[10px] mt-1" style={{ color: '#94A3B8' }}>
        <span>Pickup</span>
        <span className="font-semibold" style={{ color: '#00A86B' }}>{LOAD_LABELS[String(status || '').toLowerCase()] || status || '—'}</span>
        <span>Delivery</span>
      </div>
    </div>
  );
}

function ClientJobDetail({ onLogout }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [job, setJob] = useState(null);
  const [loads, setLoads] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await fetch(`/client/jobs/${id}`);
        const d = await r.json();
        if (cancelled) return;
        if (!d.success) { setError(d.error || 'This job could not be found.'); return; }
        setJob(d.job);
        setLoads(d.loads || []);
      } catch (err) {
        if (!cancelled) setError('Could not load this job. Please refresh and try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [id]);

  if (loading) {
    return (
      <ClientLayout activeTab="jobs" onLogout={onLogout} user={user}>
        <div className="py-16 text-center text-gray-400"><FaSpinner className="animate-spin inline" size={24} /></div>
      </ClientLayout>
    );
  }

  if (error) {
    return (
      <ClientLayout activeTab="jobs" onLogout={onLogout} user={user}>
        <Link to="/client-jobs" className="mb-4 inline-flex items-center gap-2 text-gray-500 hover:text-navy">
          <FaArrowLeft /> All jobs
        </Link>
        <div className="bg-white rounded-xl border p-10 text-center" style={{ borderColor: '#E8ECEF' }}>
          <div className="text-5xl mb-3">📭</div>
          <p className="text-sm text-gray-500">{error}</p>
        </div>
      </ClientLayout>
    );
  }

  const meta = STATUS_META[String(job.status || '').toLowerCase()] || { label: job.status || '—', color: '#64748b', bg: '#f1f5f9' };
  // Not every job is measured in tonnes - see utils/jobUnits.
  const units = jobUnits(job);
  const collectedPct = units.hasProgress ? Math.min(Math.round((units.done / units.total) * 100), 100) : 0;
  const doneLoads = loads.filter(l => ['delivered', 'completed'].includes(String(l.status || '').toLowerCase())).length;

  return (
    <ClientLayout activeTab="jobs" onLogout={onLogout} user={user}>
      <Link to="/client-jobs" className="mb-4 inline-flex items-center gap-2 text-gray-500 hover:text-navy">
        <FaArrowLeft /> All jobs
      </Link>

      {/* ── Header ── */}
      <div className="bg-white rounded-2xl border p-6 mb-4" style={{ borderColor: '#E8ECEF' }}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-xs text-gray-400 font-medium">{job.quote_id ? 'From your accepted quotation' : 'Job'}</p>
            <h1 className="text-2xl font-bold" style={{ color: '#0A1929' }}>{job.job_number}</h1>
            <p className="text-sm mt-1" style={{ color: '#4A5568' }}>{job.commodity || '—'}</p>
          </div>
          <span className="px-3 py-1 rounded-full text-xs font-semibold" style={{ color: meta.color, backgroundColor: meta.bg }}>{meta.label}</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-5">
          <div>
            <p className="text-[11px] uppercase tracking-wide text-gray-400 flex items-center gap-1"><FaMapMarkerAlt size={10} /> From</p>
            <p className="text-sm font-medium" style={{ color: '#0A1929' }}>{job.origin || '—'}</p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wide text-gray-400 flex items-center gap-1"><FaMapMarkerAlt size={10} /> To</p>
            <p className="text-sm font-medium" style={{ color: '#0A1929' }}>{job.destination || '—'}</p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wide text-gray-400 flex items-center gap-1"><FaCalendarAlt size={10} /> Started</p>
            <p className="text-sm font-medium" style={{ color: '#0A1929' }}>{fmtDate(job.start_date)}</p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wide text-gray-400 flex items-center gap-1"><FaCalendarAlt size={10} /> Expected end</p>
            <p className="text-sm font-medium" style={{ color: '#0A1929' }}>{fmtDate(job.estimated_end_date)}</p>
          </div>
        </div>

        {units.hasProgress ? (
          <div className="mt-5">
            <div className="flex justify-between text-xs mb-1" style={{ color: '#4A5568' }}>
              <span className="flex items-center gap-1"><FaWeightHanging size={11} /> {units.done} of {units.total} MT collected</span>
              <span className="font-semibold" style={{ color: '#00A86B' }}>{collectedPct}%</span>
            </div>
            <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${collectedPct}%`, backgroundColor: '#00A86B' }} />
            </div>
          </div>
        ) : (
          <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm" style={{ color: '#4A5568' }}>
            {units.kind === 'container' && (
              <span className="flex items-center gap-1"><FaBox size={12} /> {units.short}</span>
            )}
            <span className="flex items-center gap-1"><FaTruck size={12} /> {loads.length} load{loads.length === 1 ? '' : 's'} on this job</span>
          </div>
        )}
      </div>

      {/* ── Your purchase order (only when one was supplied) ── */}
      {(job.purchase_order_number || job.purchase_order_url || job.terms_accepted_at) && (
        <div className="rounded-2xl border p-4 mb-4" style={{ borderColor: '#D1FAE9', backgroundColor: '#F0FDF9' }}>
          <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: '#00A86B' }}>Your purchase order</p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            {job.purchase_order_number && (
              <span className="text-sm" style={{ color: '#0A1929' }}>P.O.: <strong>{job.purchase_order_number}</strong></span>
            )}
            {job.purchase_order_url && (
              <a href={job.purchase_order_url} target="_blank" rel="noreferrer" className="text-sm font-medium hover:underline" style={{ color: '#00A86B' }}>
                View attached P.O.{job.purchase_order_name ? ` (${job.purchase_order_name})` : ''}
              </a>
            )}
            {job.terms_accepted_at && (
              <span className="text-xs" style={{ color: '#4A5568' }}>
                Terms accepted {fmtDate(job.terms_accepted_at)}{job.terms_accepted_name ? ` by ${job.terms_accepted_name}` : ''}
              </span>
            )}
          </div>
        </div>
      )}

      {/* ── Loads ── */}
      <div className="bg-white rounded-2xl border p-5" style={{ borderColor: '#E8ECEF' }}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold flex items-center gap-2" style={{ color: '#0A1929' }}>
            <FaTruck style={{ color: '#00A86B' }} /> Loads ({loads.length})
          </h2>
          {loads.length > 0 && (
            <span className="text-xs" style={{ color: '#4A5568' }}>{doneLoads} of {loads.length} delivered</span>
          )}
        </div>

        {loads.length === 0 ? (
          <p className="text-sm text-gray-400">
            No loads have been scheduled on this job yet. They will appear here as soon as our team allocates them.
          </p>
        ) : (
          <div className="space-y-3">
            {loads.map((l) => {
              const lm = STATUS_META[String(l.status || '').toLowerCase()] || { label: l.status || '—', color: '#64748b', bg: '#f1f5f9' };
              return (
                <div key={l.id} className="rounded-xl border p-4" style={{ borderColor: '#E8ECEF' }}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold" style={{ color: '#0A1929' }}>{l.load_number || 'Load'}</span>
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ color: lm.color, backgroundColor: lm.bg }}>{lm.label}</span>
                        {l.leg_label && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">{l.leg_label}</span>}
                      </div>
                      <p className="text-xs mt-1 flex flex-wrap items-center gap-x-3 gap-y-1" style={{ color: '#4A5568' }}>
                        <span className="flex items-center gap-1"><FaTruck size={11} /> {l.truck?.registration_number || 'Truck TBC'}</span>
                        <span className="flex items-center gap-1"><FaBox size={11} /> {l.weighbridge_weight_mt || '—'} MT</span>
                        <span className="flex items-center gap-1"><FaCalendarAlt size={11} /> {fmtDate(l.scheduled_pickup_at || l.arrival_date || l.scheduled_date || l.created_at)}</span>
                        {l.pod_status && l.pod_status !== 'pending' && (
                          <span className="flex items-center gap-1" style={{ color: '#16a34a' }}><FaCheckCircle size={11} /> POD {l.pod_status}</span>
                        )}
                      </p>
                      <LoadProgress status={l.status} />
                    </div>
                    <button
                      onClick={() => navigate(`/client-load-details/${l.id}`)}
                      className="px-4 py-2 rounded-lg border text-sm font-medium hover:bg-gray-50 flex items-center gap-2 shrink-0"
                      style={{ borderColor: '#E8ECEF', color: '#0A1929' }}
                    >
                      <FaEye size={13} /> View
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {job.client_notes && (
        <div className="bg-white rounded-2xl border p-4 mt-4" style={{ borderColor: '#E8ECEF' }}>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1 flex items-center gap-1">
            <FaClipboardList size={11} /> Notes
          </p>
          <p className="text-sm" style={{ color: '#4A5568' }}>{job.client_notes}</p>
        </div>
      )}

      <div className="mt-4">
        <Link to="/client-pods" className="inline-flex items-center gap-2 text-sm hover:underline" style={{ color: '#2563eb' }}>
          <FaFileAlt size={12} /> View your proof-of-delivery documents
        </Link>
      </div>
    </ClientLayout>
  );
}

export default ClientJobDetail;


